import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, Check, FileDown, Loader2, Pencil, Plus, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Textarea } from '@/components/ui/textarea';
import { ernaehrungAPI } from '@/lib/api';

/* 3-Monats-Erfolgsreport (E15 des Ernährungscoachings, 15.10.2026): Der Entwurf entsteht 14 Tage vor Ende der
   Mindestlaufzeit (Aufgabe „Verlängerungsgespräch“) oder per Knopf. Der Coach bestätigt oder ändert die drei Erfolge,
   schreibt die Empfehlung für die nächsten drei Monate und setzt den Foto-Haken nur nach Rückfrage beim Kunden.
   Freigeben erzeugt das PDF und legt es im Tresor des Mitglieds ab — ab dann sieht es das Mitglied in der App.
   Gerechnet wird nur auf dem Server; hier steht die Bedienung. */

const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const zahl = (v, einheit = '', stellen = 1) => (v === null || v === undefined ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })}${einheit ? ` ${einheit}` : ''}`);
const delta = (v, einheit) => (v === null || v === undefined ? '–' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${zahl(Math.abs(v), einheit)}`);
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const QUELLE = { ki: 'KI-Vorschlag', heuristik: 'Vorschlag aus den Antworten', coach: 'vom Coach' };

function Zahlen({ r }) {
  const d = r.daten || {};
  const g = d.gewicht || {}; const a = d.adhaerenz || {}; const k = d.kurse || {};
  const umfaenge = Object.values(d.umfaenge || {});
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs sm:grid-cols-3">
      <div><dt className="text-muted-foreground">Gewicht</dt><dd>{zahl(g.start, 'kg')} → {zahl(g.aktuell, 'kg')} ({delta(g.delta, 'kg')})</dd></div>
      <div><dt className="text-muted-foreground">Log-Tage</dt><dd>{a.log_tage ?? 0} von {a.tage_gesamt ?? 0} ({a.log_quote ?? 0} %)</dd></div>
      <div><dt className="text-muted-foreground">Eiweißziel</dt><dd>{a.protein_quote === null || a.protein_quote === undefined ? '–' : `${a.protein_quote} % der Log-Tage`}</dd></div>
      <div><dt className="text-muted-foreground">Check-ins</dt><dd>{a.checkins_beantwortet ?? 0} von {a.checkins_faellig ?? 0} beantwortet</dd></div>
      <div><dt className="text-muted-foreground">Kursbesuche</dt><dd>{k.gesamt ?? 0} ({zahl(k.je_woche)} je Woche)</dd></div>
      <div><dt className="text-muted-foreground">Umfänge</dt><dd>{umfaenge.length ? umfaenge.map((u) => `${u.name} ${delta(u.veraenderung, 'cm')}`).join(', ') : '–'}</dd></div>
    </dl>
  );
}

function EntwurfForm({ leadId, r, onFertig, onAbbruch }) {
  const [erfolge, setErfolge] = useState([0, 1, 2].map((i) => r.erfolge?.[i] || ''));
  const [empfehlung, setEmpfehlung] = useState(r.empfehlung || '');
  const [fotos, setFotos] = useState(!!r.fotos_freigegeben);
  const [vermerk, setVermerk] = useState(r.fotos_freigabe?.vermerk || '');
  const [laeuft, setLaeuft] = useState(false);
  const speichern = async (e) => {
    e.preventDefault();
    if (erfolge.some((x) => !x.trim())) { toast.error('Bitte alle drei Erfolge ausfüllen'); return; }
    setLaeuft(true);
    try {
      await ernaehrungAPI.erfolgsreportAendern(leadId, r.id, { erfolge: erfolge.map((x) => x.trim()), empfehlung: empfehlung.trim(), fotos_freigegeben: fotos, fotos_vermerk: fotos ? vermerk.trim() : '' });
      toast.success('Gespeichert'); onFertig();
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <form onSubmit={speichern} className="space-y-2 pt-2" aria-label="Erfolgsreport bearbeiten">
      {erfolge.map((x, i) => (
        <FormField key={i} label={`Erfolg ${i + 1}`}><Input required maxLength={300} value={x} onChange={(e) => setErfolge((alt) => alt.map((y, j) => (j === i ? e.target.value : y)))} /></FormField>
      ))}
      <FormField label="Empfehlung für die nächsten 3 Monate" hint="Steht im PDF — Pflicht vor der Freigabe.">
        <Textarea rows={3} maxLength={2000} value={empfehlung} onChange={(e) => setEmpfehlung(e.target.value)} />
      </FormField>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5" checked={fotos} onChange={(e) => setFotos(e.target.checked)} />
        <span>Foto-Vergleich ins PDF — nur, wenn das Mitglied das ausdrücklich freigegeben hat (Fortschrittsfotos, Start und jetzt)</span>
      </label>
      {fotos && <FormField label="Wie wurde die Freigabe erteilt?"><Input maxLength={300} value={vermerk} onChange={(e) => setVermerk(e.target.value)} placeholder="z. B. telefonisch am 14.10., Gespräch im Studio" /></FormField>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={laeuft}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onAbbruch} disabled={laeuft}>Abbrechen</Button>
      </div>
    </form>
  );
}

function Report({ leadId, r, darf, onNeu }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [laeuft, setLaeuft] = useState('');
  const entwurf = r.status === 'entwurf';
  const oeffnen = async () => {
    setLaeuft('pdf');
    try {
      const antwort = await ernaehrungAPI.erfolgsreportPdf(leadId, r.id);
      const url = URL.createObjectURL(antwort.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(fehlertext(err, 'PDF konnte nicht geöffnet werden')); } finally { setLaeuft(''); }
  };
  const freigeben = async () => {
    if (!window.confirm('Report freigeben? Das PDF landet im Tresor des Mitglieds und ist sofort in der App sichtbar. Danach lässt er sich nicht mehr ändern.')) return;
    setLaeuft('frei');
    try { await ernaehrungAPI.erfolgsreportFreigeben(leadId, r.id); toast.success('Freigegeben — liegt im Tresor'); onNeu(); }
    catch (err) { toast.error(fehlertext(err, 'Freigeben fehlgeschlagen')); } finally { setLaeuft(''); }
  };
  const testimonial = async () => {
    setLaeuft('testimonial');
    try { await ernaehrungAPI.erfolgsreportAendern(leadId, r.id, { testimonial_angefragt: !r.testimonial_angefragt_am }); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(''); }
  };
  return (
    <li className="px-3 py-2 text-sm space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium">{tag(r.zeitraum?.von)} – {tag(r.zeitraum?.bis)}</span>
        <Badge variant="outline" className={`text-[10px] ${entwurf ? 'border-amber-300 bg-amber-500/10 text-amber-800' : 'border-emerald-300 bg-emerald-500/10 text-emerald-800'}`}>{entwurf ? 'Entwurf' : `freigegeben ${tag(r.freigegeben_am)}`}</Badge>
        {r.anlass === 'mindestlaufzeit' && <Badge variant="outline" className="text-[10px]">Automatik · Mindestlaufzeit bis {tag(r.zeitraum?.mindestlaufzeit_bis)}</Badge>}
        {r.fotos_freigegeben && <Badge variant="outline" className="text-[10px]">mit Fotos</Badge>}
        {r.testimonial_angefragt_am && <Badge variant="outline" className="text-[10px]">Testimonial angefragt {tag(r.testimonial_angefragt_am)}</Badge>}
      </div>
      <Zahlen r={r} />
      {bearbeiten ? <EntwurfForm leadId={leadId} r={r} onFertig={() => { setBearbeiten(false); onNeu(); }} onAbbruch={() => setBearbeiten(false)} /> : (
        <>
          <ol className="list-decimal pl-5 text-xs space-y-0.5">
            {(r.erfolge || []).map((e, i) => <li key={i}>{e}</li>)}
          </ol>
          <p className="text-xs text-muted-foreground">
            Erfolge: {QUELLE[r.erfolge_quelle] || r.erfolge_quelle}{r.empfehlung ? ` · Empfehlung: ${r.empfehlung}` : ' · Empfehlung fehlt noch'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" className="h-7" onClick={oeffnen} disabled={!!laeuft}>{laeuft === 'pdf' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}{entwurf ? 'Vorschau (PDF)' : 'PDF öffnen'}</Button>
            {darf && entwurf && <Button size="sm" variant="outline" className="h-7" onClick={() => setBearbeiten(true)} disabled={!!laeuft}><Pencil className="w-3.5 h-3.5 mr-1" />Erfolge & Empfehlung</Button>}
            {darf && entwurf && <Button size="sm" className="h-7" onClick={freigeben} disabled={!!laeuft || !r.empfehlung}>{laeuft === 'frei' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Freigeben → Tresor</Button>}
            {darf && !entwurf && <Button size="sm" variant="ghost" className="h-7" onClick={testimonial} disabled={!!laeuft} title="Nur ein Vermerk — das CRM verschickt nichts">{r.testimonial_angefragt_am ? 'Testimonial-Vermerk zurücknehmen' : 'Testimonial angefragt (Vermerk)'}</Button>}
          </div>
        </>
      )}
    </li>
  );
}

export function ErfolgsreportKarte({ leadId }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const laden = useCallback(async () => {
    try { setD((await ernaehrungAPI.erfolgsreports(leadId)).data); setFehler(''); }
    catch (e) { setFehler(fehlertext(e, 'Erfolgsreports konnten nicht geladen werden')); }
  }, [leadId]);
  useEffect(() => { laden(); }, [laden]);
  const anlegen = async () => {
    setLaeuft(true);
    try { await ernaehrungAPI.erfolgsreportAnlegen(leadId); toast.success('Entwurf angelegt'); laden(); }
    catch (e) { toast.error(fehlertext(e, 'Entwurf konnte nicht angelegt werden')); } finally { setLaeuft(false); }
  };
  const reports = d?.reports || [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4" aria-hidden="true" />3-Monats-Erfolgsreport
          {reports.length > 0 && <Badge variant="outline">{reports.length}</Badge>}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Start vs. jetzt, Dranbleiben, Kursbesuche, drei Erfolge und die Empfehlung für die nächsten drei Monate — als PDF im
          P1-Stil. Der Entwurf entsteht 14 Tage vor Ende der Mindestlaufzeit von selbst (Aufgabe „Verlängerungsgespräch“) oder hier
          per Knopf; freigegeben liegt er im Tresor des Mitglieds.
          {d && (d.ki ? <span className="inline-flex items-center gap-1 ml-1"><Sparkles className="w-3 h-3" aria-hidden="true" />Erfolge per KI vorgeschlagen.</span> : ' Erfolge werden aus den Check-in-Antworten vorgeschlagen (ohne KI).')}
        </p>
        {fehler && !d && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
        {d && !reports.length && <p className="text-sm text-muted-foreground">Noch kein Report.</p>}
        {reports.length > 0 && (
          <ul className="divide-y rounded-md border">
            {reports.map((r) => <Report key={r.id} leadId={leadId} r={r} darf={!!d.darf_bearbeiten} onNeu={laden} />)}
          </ul>
        )}
        {d?.darf_bearbeiten && <Button size="sm" variant="outline" onClick={anlegen} disabled={laeuft}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Entwurf anlegen</Button>}
      </CardContent>
    </Card>
  );
}

export default ErfolgsreportKarte;
